// Frontend mirror of the Class Audit resolution states (docs/projects/class-audit-design.md
// §B.7, §6, §C.0). DISPLAY-ONLY / PURE: the server is the source of truth (the live-resolved read
// and the write endpoints in seasonManagerAPIs.js); this derives the grid Status chip, its
// ownership color, the filter categories, and the "needs work" flags from a class row.
//
// PHASE 1-I — TWO audited values, evaluated INDEPENDENTLY:
//   * LEVEL (SeasonManager-owned): a "Missing level" issue when mapping_source === 'unassigned'.
//   * INSTRUCTOR (Dash-owned): an "Instructor not tracked" issue when instructor_configured is
//     false -- i.e. Dash event staffing can't associate an instructor for reporting. This is NOT a
//     claim the class has no instructor; the fix belongs in Dash, not SeasonManager.
//
// Combined status (the single loud triage signal):
//   * both issues            -> Multiple issues        (mixed ownership)
//   * level only             -> Missing level          (SeasonManager)
//   * instructor only        -> Instructor not tracked (Dash)
//   * neither issue          -> a QUIET, informational label from the mapping-source provenance
//                               (Automatically mapped / Manual assignment / Manual override).
// The mapping-source resolved states (auto_suggestion / manual_assignment / manual_override) are
// NEVER failures -- they are informational (they drive the lens filters, not the to-do queues).
//
// warehouse_refresh_pending is a SEPARATE, ORTHOGONAL dimension (reporting/warehouse lag) and never
// turns a class into a quality failure.
//
// An unrecognized / missing mapping_source surfaces as a VISIBLE 'unknown' failure -- never
// silently treated as configured.

// The mapping_source values the warehouse / live-resolution read layer can emit (§B.2).
export const MAPPING_SOURCE = {
  UNASSIGNED: 'unassigned',
  AUTO_SUGGESTION: 'auto_suggestion',
  MANUAL_ASSIGNMENT: 'manual_assignment',
  MANUAL_OVERRIDE: 'manual_override',
};

// Coarse category for filtering (quick views) and styling (chip color / ownership).
export const STATUS_CATEGORY = {
  // Issue categories (loud):
  MISSING_LEVEL: 'missing-level',
  AMBIGUOUS_MAPPING: 'ambiguous-mapping',
  INSTRUCTOR_NOT_TRACKED: 'instructor-not-tracked',
  MULTIPLE_ISSUES: 'multiple-issues',
  // Resolved, informational categories (quiet) -- mapping-source provenance:
  AUTOMATICALLY_MAPPED: 'automatically-mapped',
  MANUAL_ASSIGNMENT: 'manual-assignment',
  MANUAL_OVERRIDE: 'manual-override',
  // Safety net for an unrecognized / missing mapping_source:
  UNKNOWN: 'unknown',
};

// Who fixes the issue. Drives the Status chip's ownership color and the panel's fix-location.
export const OWNER = {
  SEASONMANAGER: 'seasonmanager', // amber -- fix here (assign a level)
  DASH: 'dash',                   // blue  -- fix in Dash (staff the instructor)
  MIXED: 'mixed',                 // red   -- both
  NONE: 'none',                   // neutral -- resolved / informational
};

// Provenance label + category for each KNOWN resolved mapping_source (the quiet, informational
// state shown when a class has no issues). Anything not listed falls through to UNKNOWN_STATUS.
const RESOLVED_BY_SOURCE = {
  [MAPPING_SOURCE.AUTO_SUGGESTION]:   { label: 'Automatically mapped', category: STATUS_CATEGORY.AUTOMATICALLY_MAPPED },
  [MAPPING_SOURCE.MANUAL_ASSIGNMENT]: { label: 'Manual assignment',    category: STATUS_CATEGORY.MANUAL_ASSIGNMENT },
  [MAPPING_SOURCE.MANUAL_OVERRIDE]:   { label: 'Manual override',      category: STATUS_CATEGORY.MANUAL_OVERRIDE },
};

const UNKNOWN_STATUS = { label: 'Unknown status', category: STATUS_CATEGORY.UNKNOWN };

// --- individual checks (each reads ITS OWN dimension directly, so a both-issues row is counted
//     by BOTH the Missing Levels and Missing Instructors views) ---

// LEVEL check: a class has a missing-level issue only when it has no effective canonical level.
export function hasMissingLevel(row) {
  return (row == null ? undefined : row.mapping_source) === MAPPING_SOURCE.UNASSIGNED;
}

// A mapping_source we don't recognize (or a null row) -- a data anomaly to surface, not hide.
function hasUnknownLevel(row) {
  const source = row == null ? undefined : row.mapping_source;
  return source !== MAPPING_SOURCE.UNASSIGNED
    && !Object.prototype.hasOwnProperty.call(RESOLVED_BY_SOURCE, source);
}

// AMBIGUITY check (SeasonManager-owned, LEVEL lane). is_ambiguous_match comes from the warehouse:
// two or more EQUALLY-RANKED mapping rules matched this class and implied DIFFERENT levels, so the
// winner was picked by a technical tie-break rather than by any rule being more specific.
//
// UNRESOLVED means the rules tie AND no human has settled it. An active manual value makes the
// effective level an explicit decision, so the tie stops being an open question -- the same scoping
// the dbt build gate uses, which keeps this UI and that backstop in agreement by construction.
// Derived from mapping_source (which the read layer resolves LIVE from the override table) rather
// than from a stored override id, so assigning a level quiets the row immediately.
//
// A missing/undefined flag is NOT fabricated into an issue. Note that ambiguity always implies a
// suggestion exists, so this can never coexist with a missing level (verified: 0 ambiguous rows
// without a suggestion) -- the two level-lane issues are mutually exclusive by construction.
export function hasUnresolvedAmbiguity(row) {
  if (row == null) return false;
  const flag = row.is_ambiguous_match;
  if (flag !== true && flag !== 1) return false;
  const source = row.mapping_source;
  return source !== MAPPING_SOURCE.MANUAL_OVERRIDE && source !== MAPPING_SOURCE.MANUAL_ASSIGNMENT;
}

// Raw warehouse flag, regardless of whether a human has resolved it. Drives the panel's QUIET
// provenance note on a class whose manual override is holding an underlying rule conflict at bay.
function isAmbiguousMatch(row) {
  const flag = row == null ? undefined : row.is_ambiguous_match;
  return flag === true || flag === 1;
}

// INSTRUCTOR check (Dash-owned): the warehouse cannot associate an instructor from Dash event
// staffing (instructor_configured is false/0). A missing/undefined field is NOT fabricated into an
// issue -- only an explicit "not configured" counts.
export function hasInstructorNotTracked(row) {
  if (row == null) return false;
  const v = row.instructor_configured;
  if (v === undefined || v === null) return false;
  return v === false || v === 0;
}

// Derive the full Class Audit status for one class row: the combined loud/quiet triage status,
// its ownership, the individual issue signals, and the needs-attention / fully-configured
// predicates. warehouse_refresh_pending is carried separately and never affects isFailure.
export function getClassStatus(row) {
  const source = row == null ? undefined : row.mapping_source;
  const levelMissing = hasMissingLevel(row);
  const levelUnknown = hasUnknownLevel(row);
  const ambiguous = isAmbiguousMatch(row);
  const ambiguityUnresolved = hasUnresolvedAmbiguity(row);
  const instructorNotTracked = hasInstructorNotTracked(row);
  const needsAttention = levelMissing || levelUnknown || ambiguityUnresolved || instructorNotTracked;

  // Informational provenance (quiet label + lens category) for a resolved level.
  const resolved = Object.prototype.hasOwnProperty.call(RESOLVED_BY_SOURCE, source)
    ? RESOLVED_BY_SOURCE[source]
    : UNKNOWN_STATUS;

  // A missing level and an unresolved ambiguity are BOTH SeasonManager-owned level-lane issues, so
  // either one combines with the Dash-owned instructor gap into Multiple issues. They are mutually
  // exclusive (ambiguity implies a suggestion), so the order between them below never arbitrates a
  // real overlap.
  const levelIssue = levelMissing || ambiguityUnresolved;

  let category, label, owner, isFailure;
  if (levelUnknown) {
    // Data anomaly: surface distinctly and always. Investigated on the SeasonManager (level) side.
    category = STATUS_CATEGORY.UNKNOWN; label = UNKNOWN_STATUS.label; owner = OWNER.SEASONMANAGER; isFailure = true;
  } else if (levelIssue && instructorNotTracked) {
    category = STATUS_CATEGORY.MULTIPLE_ISSUES; label = 'Multiple issues'; owner = OWNER.MIXED; isFailure = true;
  } else if (levelMissing) {
    category = STATUS_CATEGORY.MISSING_LEVEL; label = 'Missing level'; owner = OWNER.SEASONMANAGER; isFailure = true;
  } else if (ambiguityUnresolved) {
    category = STATUS_CATEGORY.AMBIGUOUS_MAPPING; label = 'Ambiguous mapping'; owner = OWNER.SEASONMANAGER; isFailure = true;
  } else if (instructorNotTracked) {
    category = STATUS_CATEGORY.INSTRUCTOR_NOT_TRACKED; label = 'Instructor not tracked'; owner = OWNER.DASH; isFailure = true;
  } else {
    // Neither issue -> resolved / informational. The quiet chip shows the mapping-source provenance.
    category = resolved.category; label = resolved.label; owner = OWNER.NONE; isFailure = false;
  }

  return {
    mappingSource: source == null ? null : source,
    // combined triage status (loud when an issue; quiet informational provenance when resolved)
    category,
    label,
    owner,
    isFailure,
    // individual issue signals (for the issue-adaptive panel + quick views)
    levelMissing,
    levelUnknown,
    // raw warehouse flag; true even once a human has resolved it (drives the quiet panel note)
    ambiguous,
    // the ACTIONABLE state: rules tie AND nobody has settled it
    ambiguityUnresolved,
    // an underlying rule conflict that an active manual value is currently holding resolved
    ambiguityResolvedByOverride: ambiguous && !ambiguityUnresolved,
    instructorNotTracked,
    needsAttention,
    fullyConfigured: !needsAttention,
    // informational mapping-source provenance (for the lens filters + reference)
    mappingSourceCategory: resolved.category,
    mappingSourceLabel: resolved.label,
    // orthogonal reporting/warehouse-lag flag -- NOT a quality failure
    warehouseRefreshPending: Boolean(row && row.warehouse_refresh_pending),
  };
}

// Does this class currently need work in the Class Audit queue? True when EITHER audited value has
// an issue (missing level and/or instructor not tracked), or the mapping_source is unrecognized.
export function needsAttention(row) {
  return getClassStatus(row).needsAttention;
}

// Fully configured: neither issue is present (known-resolved level AND instructor tracked).
export function isFullyConfigured(row) {
  return getClassStatus(row).fullyConfigured;
}
